import {
  MAX_FILE_SIZE_BYTES,
  MAX_DOCUMENTS_TOTAL_SIZE_MESSAGE,
  getDocumentsTotalSize,
  getSupportingDocuments,
} from "./document-policy";
import { EMAIL_REGEX } from "../config/constants";
import { normalizeText, normalizeDocumentByType } from "./identity-utils";
import {
  getRequiredDocumentRequirements,
  getRequiredSupportingDocumentsCount,
  getRequiredDocumentsCountMessage,
} from "../config/document-requirements";

export const FIELD_ERROR_PRIORITY = [
  "isMedical",
  "specialities",
  "personType",
  "documentNumber",
  "businessName",
  "commercialName",
  "branchName",
  "physicalFirstName",
  "physicalLastName",
  "physicalExequatur",
  "physicalAddress",
  "physicalProvince",
  "physicalMunicipality",
  "physicalEmail",
  "physicalCellphone",
  "businessEmail",
  "businessCellphone",
  "businessAddress",
  "businessProvince",
  "businessMunicipality",
  "repDocumentNumber",
  "repFirstName",
  "repLastName",
  "repEmail",
  "repCellphone",
  "specialities",
  "specialityEvidence",
  "documents",
  "documentRequirements",
  "legalChecks",
];

export const getOrderedValidationMessages = (errors = {}) => {
  const orderedKeys = [
    ...FIELD_ERROR_PRIORITY,
    ...Object.keys(errors).filter((key) => !FIELD_ERROR_PRIORITY.includes(key)),
  ];

  return orderedKeys
    .map((key) => errors[key])
    .filter(Boolean)
    .filter((message, index, messages) => messages.indexOf(message) === index);
};

export const getFirstValidationMessage = (errors) =>
  getOrderedValidationMessages(errors)[0] || "";

export const buildSubmitBlockers = ({
  form,
  validationErrors,
  identityValidation,
  representativeValidation,
}) => {
  const blockers = [];

  if (identityValidation.status !== "validated") {
    blockers.push(
      "Debe validar el documento antes de continuar con los pasos de datos y documentos.",
    );
  }

  if (
    form.personType === "MORAL" &&
    representativeValidation.status !== "validated"
  ) {
    blockers.push(
      "Debe validar la cédula o pasaporte del representante legal para continuar.",
    );
  }

  return [...blockers, ...getOrderedValidationMessages(validationErrors)].filter(
    (message, index, messages) => messages.indexOf(message) === index,
  );
};

export const buildValidationErrors = ({
  form,
  documents,
  documentRequirementsCompleted,
  legalChecksCompleted,
}) => {
  const errors = {};

  if (!normalizeText(form.personType)) {
    errors.personType = "Seleccione la personalidad juridica para continuar.";
  }

  if (form.isMedical !== true && form.isMedical !== false) {
    errors.isMedical = "Indique si el solicitante es medico o no.";
  }

  const selectedSpecialities = (form.specialities || []).filter(
    (speciality) => Number(speciality.id || 0) > 0,
  );
  if (form.isMedical === true && !selectedSpecialities.length) {
    errors.specialities = "Debe agregar al menos una especialidad médica a la tabla.";
  }
  (form.specialities || []).forEach((speciality, index) => {
    if (!Number(speciality.id || 0)) {
      errors[`speciality-${index}`] = "Seleccione una especialidad medica.";
    }
  });
  const specialityIds = selectedSpecialities.map((speciality) => Number(speciality.id));
  if (new Set(specialityIds).size !== specialityIds.length) {
    errors.specialities = "No puede seleccionar la misma especialidad más de una vez.";
  }
  if (form.isMedical === true && form.specialityDraftPending) {
    errors.specialities =
      "Debe presionar Agregar o Guardar cambios para completar la especialidad en captura.";
  }

  if (!normalizeText(form.documentNumber)) {
    errors.documentNumber =
      form.personType === "MORAL"
        ? "Debe indicar el RNC."
        : "Debe indicar la cédula o pasaporte.";
  }

  if (!normalizeText(form.businessName)) {
    errors.businessName = "Debe indicar la razón social.";
  }

  if (!normalizeText(form.branchName)) {
    errors.branchName = "Debe indicar la sucursal principal.";
  }

  if (form.personType === "FISICA") {
    const requiredPhysicalFields = [
      ["physicalFirstName", "El primer nombre es obligatorio."],
      ["physicalLastName", "El primer apellido es obligatorio."],
      ["physicalAddress", "La direccion es obligatoria."],
      ["physicalProvince", "Seleccione una provincia."],
      ["physicalMunicipality", "Seleccione un municipio."],
      ["physicalEmail", "El correo electrónico es obligatorio."],
      ["physicalCellphone", "El celular es obligatorio."],
    ];

    requiredPhysicalFields.forEach(([fieldName, message]) => {
      if (!normalizeText(form[fieldName])) {
        errors[fieldName] = message;
      }
    });

    if (form.isMedical === true && !normalizeText(form.physicalExequatur)) {
      errors.physicalExequatur = "Debe completar el exequátur del medico.";
    }

    if (normalizeText(form.physicalEmail) && !EMAIL_REGEX.test(normalizeText(form.physicalEmail))) {
      errors.physicalEmail = "El correo electrónico no tiene un formato válido.";
    }
  }

  if (form.personType === "MORAL") {
    const requiredMoralFields = [
      ["businessEmail", "El correo electrónico del cliente es obligatorio."],
      ["businessCellphone", "El celular del cliente es obligatorio."],
      ["businessAddress", "La direccion del cliente es obligatoria."],
      ["businessProvince", "Seleccione una provincia."],
      ["businessMunicipality", "Seleccione un municipio."],
      ["repDocumentNumber", "El documento del representante es obligatorio."],
      ["repFirstName", "El primer nombre del representante es obligatorio."],
      ["repLastName", "El primer apellido del representante es obligatorio."],
      ["repEmail", "El correo electrónico del representante es obligatorio."],
      ["repCellphone", "El celular del representante es obligatorio."],
    ];

    requiredMoralFields.forEach(([fieldName, message]) => {
      if (!normalizeText(form[fieldName])) {
        errors[fieldName] = message;
      }
    });

    if (normalizeText(form.businessEmail) && !EMAIL_REGEX.test(normalizeText(form.businessEmail))) {
      errors.businessEmail = "El correo electrónico del cliente no tiene un formato válido.";
    }

    if (normalizeText(form.repEmail) && !EMAIL_REGEX.test(normalizeText(form.repEmail))) {
      errors.repEmail = "El correo electrónico del representante no tiene un formato válido.";
    }

    if (form.isMedical === true && !normalizeText(form.physicalExequatur)) {
      errors.physicalExequatur = "Debe completar el exequátur del medico.";
    }
  }

  const normalizedDocument = normalizeDocumentByType(form.documentNumber, form.documentType);

  if (
    normalizeText(form.documentNumber) &&
    form.documentType === "CEDULA" &&
    normalizedDocument.length !== 11
  ) {
    errors.documentNumber = "La cédula debe contener 11 digitos.";
  }

  if (
    normalizeText(form.documentNumber) &&
    form.documentType === "RNC" &&
    normalizedDocument.length !== 9
  ) {
    errors.documentNumber = "El RNC debe contener 9 digitos.";
  }

  if (
    normalizeText(form.documentNumber) &&
    form.documentType === "PASAPORTE" &&
    !/^[A-Z0-9]{6,20}$/i.test(normalizedDocument)
  ) {
    errors.documentNumber = "El pasaporte debe ser alfanumerico y tener entre 6 y 20 caracteres.";
  }

  if (form.personType === "MORAL" && normalizeText(form.repDocumentNumber)) {
    const normalizedRepDocument = normalizeDocumentByType(
      form.repDocumentNumber,
      form.repDocumentType,
    );

    if (form.repDocumentType === "CEDULA" && normalizedRepDocument.length !== 11) {
      errors.repDocumentNumber = "La cédula del representante debe contener 11 digitos.";
    }

    if (
      form.repDocumentType === "PASAPORTE" &&
      !/^[A-Z0-9]{6,20}$/i.test(normalizedRepDocument)
    ) {
      errors.repDocumentNumber =
        "El pasaporte del representante debe ser alfanumerico y tener entre 6 y 20 caracteres.";
    }
  }

  if (form.isMedical === true) {
    const selectedSpecialityIds = new Set(
      (form.specialities || []).map((speciality) => Number(speciality.id)),
    );
    if (documents.some((doc) =>
      doc.requirementKey === "specialtyDegreeMsp" &&
      !selectedSpecialityIds.has(Number(doc.specialityId))
    )) {
      errors.specialities =
        "Complete la especialidad en captura con el boton Agregar o cancele la captura.";
    }
    (form.specialities || []).forEach((speciality, index) => {
      if (speciality.id && !documents.some((doc) =>
        doc.requirementKey === "specialtyDegreeMsp" &&
        Number(doc.specialityId) === Number(speciality.id)
      )) {
        errors[`specialityEvidence-${index}`] =
          "Adjunte al menos una evidencia para esta especialidad.";
      }
    });
  }

  const documentsTotalSize = getDocumentsTotalSize(documents);
  const requiredDocumentsCount = getRequiredSupportingDocumentsCount(form);
  const supportingDocuments = getSupportingDocuments(documents);

  if (documentsTotalSize >= MAX_FILE_SIZE_BYTES) {
    errors.documents = MAX_DOCUMENTS_TOTAL_SIZE_MESSAGE;
  } else if (
    requiredDocumentsCount &&
    supportingDocuments.length !== requiredDocumentsCount
  ) {
    errors.documents = getRequiredDocumentsCountMessage(requiredDocumentsCount);
  } else if (!requiredDocumentsCount && !documents.length) {
    errors.documents = "Debe adjuntar al menos un documento.";
  }

  if (getRequiredDocumentRequirements(form).length && !documentRequirementsCompleted) {
    errors.documentRequirements =
      "Debe marcar los checks de documentos requeridos para este tipo de preregistro.";
  }

  if (!legalChecksCompleted) {
    errors.legalChecks =
      "Debe aceptar Términos y Condiciones y Política de Privacidad para continuar.";
  }

  return errors;
};
